<template>
  <SysDialog v-model="dialog_ativo_computed" :largura="600">
    <template #titulo>
      <div>
        <p class="ma-0 text-gray-900">Exportação Cisspoder Integrim</p>
        <p class="ma-0 mt-n3 pl-1px font-12 text-gray-600 font-weight-regular">
          {{ `Exportação do pedido #${pedido.idpedido} com ${produtos_pedido.length} produtos` }}
        </p>
      </div>

      <v-spacer />

      <v-btn
        icon
        :disabled="etapa_atual_exportacao === etapas_exportacao.exportando"
        @click="dialog_ativo_computed = false"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </template>

    <div v-if="dialog_ativo_computed" class="pb-3">
      <div v-if="etapa_atual_exportacao === etapas_exportacao.nao_exportado">
        <SysFilialGrupoAutocomplete
          v-model="exportacao.filial"
          label="Selecione a Filial"
          outlined
        />

        <SysFornecedorFabricanteAutocomplete
          v-model="exportacao.fornecedor_fabricante"
          label="Selecione o Fornecedor"
          outlined
          :idpedido="Number(pedido.idpedido)"
          :idfabricante="Number(pedido.idfornecedor)"
          analise-grupo-fornecedores="N"
        />

        <SysDatePicker
          v-model="exportacao.data_entrega"
          titulo_label="Informe a Data da Entrega"
          outlined
          hide-details
          :data_minima="data_atual"
        />
      </div>

      <div v-else-if="etapa_atual_exportacao === etapas_exportacao.exportando" class="py-70px">
        <div class="text-center">
          <v-progress-circular color="red" indeterminate size="50" />
          <p class="ma-0 mt-4">Exportando... aguarde.</p>
        </div>
      </div>

      <ResultadoExportacaoComSucesso
        v-else-if="etapa_atual_exportacao === etapas_exportacao.exportado"
        :idpedido_systock="Number(pedido.idpedido)"
      />

      <div v-else-if="produtos_invalidos_computed.length > 0" class="py-51px text-center">
        <v-icon size="80" color="red" class="my-3">mdi-file-alert</v-icon>
        <p class="ma-0 font-weight-medium text-red-900" v-text="`${produtos_invalidos_computed.length} produtos inválidos para exportação`" />
      </div>

      <div v-else class="py-51px text-center">
        <v-icon size="80" color="red" class="my-3">mdi-alert</v-icon>
        <p class="ma-0 font-weight-medium text-red-900">
          {{ etapa_atual_exportacao === etapas_exportacao.erro ? detalhes_erro.mensagem : 'Erro desconhecido' }}
        </p>
      </div>
    </div> 

    <template #actions>
      <v-btn v-if="etapa_atual_exportacao === etapas_exportacao.exportado" text color="primary" @click="dialog_ativo_computed = false;">
        OK, FECHAR
      </v-btn>

      <v-btn v-else text color="primary" :disabled="!ativo_para_exportacao_computed" @click="exportarPedido">
        {{ etapa_atual_exportacao === etapas_exportacao.erro ? 'TENTAR NOVAMENTE' : 'OK, EXPORTAR' }}
      </v-btn>
    </template>
  </SysDialog>
</template>

<script>
import {computed, ref, watch} from 'vue';
import api from "@/api/api.js";
import SysDialog from "@/components/padraoDeLayout/SysDialog.vue";
import SysFilialGrupoAutocomplete from "@/components/utils/crud/SysFilialGrupoAutocomplete.vue";
import SysFornecedorFabricanteAutocomplete from '@/components/utils/crud/SysFornecedorFabricanteAutocomplete.vue';
import SysDatePicker from '@/components/utils/SysDatePicker.vue';
import ResultadoExportacaoComSucesso from '@/components/compra/carrinho/Exportacoes/ExportacaoCissPoderIntegrim/ResultadoExportacaoComSucesso.vue';
import {logout} from "@/helpers";

export default {
  name: "ExportacaoCissPoderIntegrim",
  components: {
    SysDialog,
    SysFilialGrupoAutocomplete,
    SysFornecedorFabricanteAutocomplete,
    SysDatePicker,
    ResultadoExportacaoComSucesso,
  },
  model: {
    prop: "modelValue",
    event: "update:modelValue",
  },
  props: {
    modelValue: Boolean,
    pedido: Object,
    produtos_pedido: Array,
  },
  emits: ['update:modelValue', 'exportado-com-sucesso'],
  setup(props, { emit }) {
    const data_atual = localStorage.data_atual.split("/").reverse().join("-");

    const etapas_exportacao = {
      erro: 0,
      nao_exportado: 1,
      exportando: 2,
      exportado: 3,
    };

    const etapa_atual_exportacao = ref(etapas_exportacao.nao_exportado);

    const exportacao = ref({
      filial: '',
      fornecedor_fabricante: '',
      data_entrega: '',
    });

    const dialog_ativo_computed = computed({
      get: () => props.modelValue,
      set: valor => emit('update:modelValue', valor),
    });
    watch(dialog_ativo_computed, dialog => {
      if (dialog) {
        buscarResultadoExportacao();
        return;
      }

      if (etapa_atual_exportacao.value === etapas_exportacao.exportado) {
        emit('exportado-com-sucesso', {});
      }

      resetarEstadoDialog();
    });

    const pedido_ja_exportado = computed(() => String(props.pedido.pedido_cli) !== '0');

    const produtos_invalidos_computed = computed(() => {
      const produto_invalido = produto => (produto.preco_custo <= 0 || produto.qtde_pedido <= 0)
      return props.produtos_pedido.filter(produto_invalido)
    });

    const ativo_para_exportacao_computed = computed(() => {
      return exportacao.value.filial
        && exportacao.value.fornecedor_fabricante
        && exportacao.value.data_entrega
        && produtos_invalidos_computed.value.length === 0
    });

    function resetarEstadoDialog() {
      exportacao.value.filial = '';
      exportacao.value.fornecedor_fabricante = '';
      exportacao.value.data_entrega = '';
      etapa_atual_exportacao.value = pedido_ja_exportado.value ? etapas_exportacao.exportado : etapas_exportacao.nao_exportado;
    }

    function buscarResultadoExportacao() {
      api.get(`pedidos-compra/${props.pedido.idpedido}/exportacao/cisspoder-integrim/resultado-exportacao`)
        .then(res => {
          if (! res.data.informacoes_extra) return;

          const {filial_pedido_exportado, idfornecedor_pedido, data_entrega} = res.data.informacoes_extra;
          exportacao.value.filial = filial_pedido_exportado;
          exportacao.value.fornecedor_fabricante = idfornecedor_pedido;

          exportacao.value.data_entrega = data_entrega;
          etapa_atual_exportacao.value = etapas_exportacao.erro;
          detalhes_erro.value.mensagem = 'Erro na exportação anterior.';
        })
        .catch(logout);
    }

    const detalhes_erro = ref({
      mensagem: ''
    });

    function exportarPedido() {
      etapa_atual_exportacao.value = etapas_exportacao.exportando;

      const parametros = {
        idpedido: props.pedido.idpedido,
        ...exportacao.value,
      };

      api.post(`pedidos-compra/${parametros.idpedido}/exportacao/cisspoder-integrim`, parametros)
        .then(() => {
          etapa_atual_exportacao.value = etapas_exportacao.exportado;
          detalhes_erro.value.mensagem = '';
        })
        .catch(erro => {
          etapa_atual_exportacao.value = etapas_exportacao.erro;
          detalhes_erro.value = erro.response.data.detalhes_erro;
        });
    }

    return {
      dialog_ativo_computed,
      produtos_invalidos_computed,
      ativo_para_exportacao_computed,
      etapas_exportacao,
      etapa_atual_exportacao,
      data_atual,
      exportacao,
      detalhes_erro,
      exportarPedido,
    };
  }
}
</script>
